import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { Radio } from 'lucide-react';

const CarrierQualityHeatmap = ({ filters, reducedMotion = false, onFilterChange }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [qualityData, setQualityData] = useState({ cells: [], rows: [], cols: [] });
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState('province');
  const animationDuration = reducedMotion ? 0 : 0.5;

  useEffect(() => {
    fetchQualityData();
  }, [filters, granularity]);

  const fetchQualityData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/analytics/carrier-quality?granularity=${granularity}`);
      const result = await response.json();
      setQualityData(result);
    } catch (error) {
      console.error('Error fetching carrier quality:', error);
    } finally {
      setLoading(false);
    }
  };

  const getColorForDelta = (delta) => {
    // Delta is change from baseline (-1 to +1)
    if (delta >= 0.1) return { bg: '#10b981', text: '#ffffff' }; // Big improvement
    if (delta >= 0.05) return { bg: '#22c55e', text: '#ffffff' }; // Good
    if (delta >= 0) return { bg: '#84cc16', text: '#000000' }; // Slight improvement
    if (delta >= -0.05) return { bg: '#eab308', text: '#000000' }; // Slight decline
    if (delta >= -0.1) return { bg: '#f97316', text: '#ffffff' }; // Concerning
    return { bg: '#ef4444', text: '#ffffff' }; // Critical drop
  };

  const handleCellClick = (cell) => {
    if (onFilterChange) {
      onFilterChange({
        carrier: cell.row,
        [granularity]: cell.col
      });
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
              <Radio className="w-5 h-5 text-ink-500" />
              Carrier Quality Heatmap
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Connection rate delta vs 7-day baseline • Click to filter
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Granularity Toggle */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'province', label: 'By Province' },
            { key: 'tz', label: 'By Timezone' }
          ].map((gran) => (
            <button
              key={gran.key}
              onClick={() => setGranularity(gran.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                granularity === gran.key
                  ? 'bg-gradient-to-r from-ink-500 to-gold text-white shadow-lg'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {gran.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-80">
            <div className="animate-pulse text-slate-400">Loading carrier quality...</div>
          </div>
        ) : qualityData.cells.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No carrier quality data available yet</p>
            <p className="text-xs mt-2">Data will appear as calls are processed</p>
          </div>
        ) : (
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: animationDuration }}
          >
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full">
                {/* Header row */}
                <div className="flex gap-1 mb-1 ml-24">
                  <div className="w-16 h-8"></div>
                  {qualityData.cols.map((col, idx) => (
                    <div key={idx} className="w-20 h-8 flex items-center justify-center">
                      <span className="text-xs text-slate-400 font-medium">{col}</span>
                    </div>
                  ))}
                </div>

                {/* Heatmap grid */}
                {qualityData.rows.map((row, rowIdx) => (
                  <div key={rowIdx} className="flex gap-1 mb-1">
                    {/* Row label */}
                    <div className="w-24 h-16 flex items-center justify-end pr-2">
                      <span className="text-xs text-slate-400 text-right font-medium">{row}</span>
                    </div>

                    {/* Cells */}
                    {qualityData.cols.map((col, colIdx) => {
                      const cell = qualityData.cells.find(c => c.row === row && c.col === col);
                      const colors = cell ? getColorForDelta(cell.delta_vs_7d) : { bg: '#1e293b', text: '#475569' };
                      
                      return (
                        <motion.div
                          key={`${rowIdx}-${colIdx}`}
                          className="w-20 h-16 rounded flex flex-col items-center justify-center cursor-pointer hover:ring-2 hover:ring-ink-500 transition-all"
                          style={{ backgroundColor: colors.bg }}
                          initial={reducedMotion ? {} : { scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{
                            duration: animationDuration,
                            delay: (rowIdx * qualityData.cols.length + colIdx) * 0.01,
                            ease: 'easeOut'
                          }}
                          whileHover={reducedMotion ? {} : { scale: 1.05 }}
                          onClick={() => cell && handleCellClick(cell)}
                        >
                          {cell && (
                            <>
                              <span className="text-sm font-bold" style={{ color: colors.text }}>
                                {(cell.conn_rate * 100).toFixed(1)}%
                              </span>
                              <span 
                                className="text-xs font-medium" 
                                style={{ color: colors.text }}
                              >
                                {cell.delta_vs_7d > 0 ? '+' : ''}{(cell.delta_vs_7d * 100).toFixed(1)}%
                              </span>
                              <span className="text-[10px]" style={{ color: colors.text, opacity: 0.7 }}>
                                n={cell.sample}
                              </span>
                            </>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-6 flex items-center justify-center gap-2 text-xs flex-wrap">
              <span className="text-slate-400">Delta vs 7d baseline:</span>
              {[
                { color: '#ef4444', label: '< -10%' },
                { color: '#f97316', label: '-10 to -5%' },
                { color: '#eab308', label: '-5 to 0%' },
                { color: '#84cc16', label: '0 to +5%' },
                { color: '#22c55e', label: '+5 to +10%' },
                { color: '#10b981', label: '> +10%' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }}></div>
                  <span className="text-slate-400">{item.label}</span>
                </div>
              ))}
            </div>

            {/* Stats */}
            {qualityData.meta && (
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-ink-500">{qualityData.rows.length || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Carriers</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gold">{qualityData.cols.length || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Regions</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{qualityData.meta.sample_size || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Calls Analyzed</p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default CarrierQualityHeatmap;
